import React from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { deleteUserProject } from '../../../modules/project';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import { useNotification } from '../../../hooks/NotificationContext';
import { useInvalidateQuery } from '../../../query/apiQueries';

export const deletionIssues = [
  'All test cases linked to the project, including steps, linked issues, and histories',
  'All test cycles, test executions, and their respective iterations',
  'All attachments (files, images, etc.) and comments linked to the project',
  'All configuration settings, including test environments and integrations',
  'All associated users and their project-specific roles and permissions',
  'Any related project data',
];

const DeleteProject = ({
  open,
  handleDeleteCancel,
  selectedProject,
  setDeleteConfirmationOpen,
  fetchProjectData,
}) => {
  const { addNotification } = useNotification();
  const invalidateQueries = useInvalidateQuery();
  const handleDeleteConfirm = async () => {
    try {
      await deleteUserProject(selectedProject.id);
      setDeleteConfirmationOpen(false);
      addNotification('success', 'Project deleted successfully!');
      fetchProjectData();
      invalidateQueries([
        { key: 'project', type: 'headerProjects' },
        { key: 'project', type: 'dashboardProjects' },
        { key: 'project', type: 'list' },
      ]);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <ConfirmationModal
      show={open}
      handleClose={handleDeleteCancel}
      handleConfirm={handleDeleteConfirm}
      title="Remove Project"
      buttonLabel="Delete"
      body={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2">
            Deleting the project{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {selectedProject.name}
            </Box>{' '}
            will permanently remove the following:
          </Typography>

          <List
            dense
            disablePadding
            sx={{
              pl: 1,
              borderLeft: '3px solid',
              borderColor: 'error.main',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            {deletionIssues.map((issue, index) => (
              <ListItem key={index} disableGutters disablePadding sx={{ alignItems: 'flex-start', gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    mt: '6px',
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={issue}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            ))}
          </List>

          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>

          <Typography variant="body2" fontWeight={600}>
            Are you sure you want to delete this project?
          </Typography>
        </Box>
      }
    />
  );
};

export default DeleteProject;
